import React, { useEffect, useRef, useState } from 'react';
import { Image, twMerge } from '@vgn-medien-holding/vgn-fe-components';
import { ArticleCategory } from '@components/atoms/ArticleCategory/ArticleCategory';
import { CircleCounter } from '@components/atoms/CircleCounter/CircleCounter';
import { FallbackCard } from '@components/atoms/FallbackCard/FallbackCard';
import { Link } from '@components/atoms/Link/Link';
import { TextCard } from '@components/molecules/TextCard/TextCard';
import { createDate } from '@utils/dateHelpers';
import { Article } from '@lib/graphql/generated';
import { IconArrow } from '@assets/icon-arrow';
import { TopStories } from '../TopStories/TopStories';

export interface ArticleHeroTeaserProps {
  articles: Article[];
}

export const ArticleHeroTeaser = ({ articles }: ArticleHeroTeaserProps) => {
  articles = articles.slice(0, 4);

  const [activeIndex, setActiveIndex] = useState(0);
  const timeoutRef = useRef(null);
  const delay = 5000;

  function resetTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }

  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setTimeout(
      () => setActiveIndex((prevIndex) => (prevIndex === articles.length - 1 ? 0 : prevIndex + 1)),
      delay,
    );
    return () => {
      resetTimeout();
    };
  }, [activeIndex, articles.length]);

  return (
    <div className="mt-0 grid sm:mt-10 lg:grid-cols-2 lg:gap-x-12 xl:grid-cols-[3fr,2fr]">
      <div className="h-full">
        <div
          className={
            'relative mx-auto h-[65vh] w-full md:h-auto md:min-h-[60vw] xl:aspect-video xl:min-h-full portrait-giant:!h-[640px] portrait-giant:!min-h-[640px]'
          }
        >
          {articles.map((article, index) => (
            <Link
              href={'/' + encodeURIComponent(article?.ressort?.slug) + '/' + encodeURIComponent(article?.slug)}
              key={article.id}
            >
              <div className="cursor-pointer">
                <div className={twMerge('absolute inset-0 hidden size-full', index === activeIndex && 'block')}>
                  {article?.cover_image?.url ? (
                    <Image
                      src={article?.cover_image?.url}
                      copyright={article?.cover_image?.copyright_text}
                      alt={article?.headline}
                      fill
                      imageMode="cover"
                      sizes="(min-width: 768px) 50vw, 100vw"
                      className="size-full overflow-hidden rounded-2xl"
                    />
                  ) : (
                    <FallbackCard classProps={{ root: 'absolute inset-0 rounded-2xl min-h-full bg-white' }} />
                  )}
                  <div className="absolute inset-x-0 bottom-0 px-4">
                    <TextCard
                      text={article.headline}
                      authors={article.hide_authors ? [] : article.authors}
                      date={createDate(article.created_at)}
                      classProps={{
                        root: 'relative m-4 mx-auto max-w-[600px] rounded-xl border-0.5 border-gray-650/25 bg-gray-200/95 p-8 cursor-pointer',
                        text: 'border-b border-gray-650/65 pb-3 text-2xl font-bold',
                        date: 'text-gray-500',
                        authorLine: 'text-gray-400',
                      }}
                    >
                      <ArticleCategory
                        classProps={{ root: 'absolute -top-3.5 left-8' }}
                        category={article.ressort}
                        noLink={true}
                      />
                      <IconArrow
                        classProps={{
                          root: 'absolute z-10 w-6 h-6 right-8 bottom-6',
                        }}
                      />
                    </TextCard>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-6 flex justify-center gap-10 lg:hidden">
          {articles.map((article, index) => (
            <span
              className="cursor-pointer"
              key={index}
              onClick={() => {
                setActiveIndex(index);
              }}
            >
              <CircleCounter size={30} strokeWidth={1.8} duration={delay} active={index === activeIndex} />
            </span>
          ))}
        </div>
      </div>
      <TopStories articles={articles} activeIndex={activeIndex} classProps={{ root: 'hidden lg:grid' }} />
    </div>
  );
};
